import React, { useCallback, useMemo, useState } from "react";
import { Alert, Form } from "react-bootstrap";
import styled from "styled-components";
import { colors } from "utils";
import { FiltersBar } from "./FiltersBar";
import { DataTable } from "./DataTable";
import { ReportToolbar } from "./ReportToolbar";
import { useCSReportRunner } from "./useCSReportRunner";
import {
  downloadDosageTrendCSV,
  fetchDosageTrend,
} from "./csReportsApi";
import {
  buildReportParams,
  deriveColumns,
  explicitColumns,
  validateBaseFilters,
} from "./reportHelpers";

const BG_GREEN = "#d1fae5";
const BG_YELLOW = "#fef3c7";
const BG_RED = "#fecaca";

// School-year buckets cover 12 months each; allow ~5 years of history.
const SCHOOL_YEAR_MAX_RANGE_DAYS = 1830;

const formatSchoolYearLabel = (value) => {
  if (!value || typeof value !== "string") return "";
  const match = value.match(/^(\d{4})-/);
  if (!match) return value;
  const startYear = parseInt(match[1], 10);
  const endShort = String((startYear + 1) % 100).padStart(2, "0");
  return `${startYear}-${endShort}`;
};

const CUSTOMER_ROLLUP_COLUMNS = [
  ["week_of", "Week of"],
  ["customer_name", "Customer"],
  ["active_teachers", "Active Teachers"],
  ["session_count", "Session Count"],
  ["session_count_gt_5_mins", "Session Count > 5 mins"],
  ["total_group_duration_mins", "Total Group Duration (min)"],
  ["total_student_duration_mins", "Total Student Duration (min)"],
  ["active_students_all", "Active Students (all)"],
  ["active_students_all_5mins", "Active Students > 5 mins"],
  ["active_students_2", "Active Students (2+)"],
  ["active_students_3", "Active Students (3+)"],
  ["active_students_green", "Active Students", { bgColor: BG_GREEN }],
  ["active_students_yellow", "Active Students", { bgColor: BG_YELLOW }],
  ["active_students_red", "Active Students", { bgColor: BG_RED }],
  ["pct_green", "Active Students", { bgColor: BG_GREEN, format: "percent" }],
  ["pct_yellow", "Active Students", { bgColor: BG_YELLOW, format: "percent" }],
  ["pct_red", "Active Students", { bgColor: BG_RED, format: "percent" }],
];

const ExtraField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const ExtraLabel = styled.label`
  color: ${colors.nightlyWoods700 || "#374151"};
  font-size: 12px;
  font-weight: 600;
  margin: 0;
  text-transform: uppercase;
`;

const ExtraGroup = styled.div`
  display: contents;
`;

export const DosageTrendTab = ({
  filters,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}) => {
  const [groupBy, setGroupBy] = useState("week");
  const rollUp = "customer";
  const isSchoolYear = groupBy === "school_year";
  const runner = useCSReportRunner({
    fetchJson: fetchDosageTrend,
    fetchCsv: downloadDosageTrendCSV,
    errorTag: "dosage-trend",
  });

  const validation = useMemo(
    () =>
      validateBaseFilters({
        filters,
        startDate,
        endDate,
        maxRangeDays: isSchoolYear ? SCHOOL_YEAR_MAX_RANGE_DAYS : undefined,
      }),
    [filters, startDate, endDate, isSchoolYear]
  );

  const params = useMemo(() => {
    if (!validation.ok) return null;
    return {
      ...buildReportParams({ filters, startDate, endDate }),
      group_by: groupBy,
      roll_up: rollUp,
    };
  }, [filters, startDate, endDate, groupBy, rollUp, validation.ok]);

  const handleGroupByChange = useCallback((next) => {
    setGroupBy(next);
    runner.reset();
  }, [runner]);

  const columns = useMemo(() => {
    if (rollUp !== "customer") return deriveColumns(runner.rows);
    const specs = CUSTOMER_ROLLUP_COLUMNS.map(([key, header, meta]) => {
      if (key === "week_of" && isSchoolYear) {
        return [
          key,
          "School Year",
          { ...(meta || {}), formatter: formatSchoolYearLabel },
        ];
      }
      return [key, header, meta];
    });
    return explicitColumns(specs, runner.rows);
  }, [rollUp, runner.rows, isSchoolYear]);

  const extraControls = (
    <ExtraGroup>
      <ExtraField>
        <ExtraLabel>Group by</ExtraLabel>
        <Form.Select
          value={groupBy}
          onChange={(e) => handleGroupByChange(e.target.value)}
        >
          <option value="day">Day</option>
          <option value="week">Week</option>
          <option value="month">Month</option>
          <option value="school_year">School Year</option>
        </Form.Select>
      </ExtraField>
    </ExtraGroup>
  );

  return (
    <div>
      <FiltersBar
        filters={filters}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={onStartDateChange}
        onEndDateChange={onEndDateChange}
        extraControls={extraControls}
      />

      <ReportToolbar
        validation={validation}
        loading={runner.loading}
        downloading={runner.downloading}
        onRun={() => runner.run(params)}
        onDownload={() => runner.download(params)}
      />

      {runner.error && <Alert variant="danger">{String(runner.error)}</Alert>}

      {runner.rows !== null && (
        <DataTable
          data={runner.rows}
          columns={columns}
          emptyMessage="No sessions in this date range for the selected filters."
        />
      )}
    </div>
  );
};
